import { ChangeEvent, MouseEvent, useState, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";

import Button from "@/components/Button";
import Text from "@/components/Text";
import { PageHeading, SrOnly } from "@/components/Layout/Parts";
import { Controls, ControlsButton } from "@/components/Controls";
import { TableHeader } from "@/components/Table";
import ThumbnailsUploadFiles from "./ThumbnailsUploadFiles";
import { hexToRgba } from "@/utils/theme";
import GeneratedThumbnaisStyles from "./GeneratedThumbnailsStyles";
import ThumbnailsUploadForm from "./ThumbnailsUploadForm";
import ThumbnailsDownloadProgress from "./ThumbnailsDownloadProgress";
import { MediaResource } from "@/types/resources";
import { api } from "@/utils/api";
import ThumbnailsLibrary from "./ThumbnailsLibrary";
import ThumbnailsImageRow from "./ThumbnailsImageRow";
import ThumbnailsGeneratedVariations from "./ThumbnailsGeneratedVariations";

enum Tabs {
  UploadElements,
  ForegroundLibrary,
  BackgroundLibrary,
}

enum TypeTabs {
  VerticalSplit,
  TextOverlay,
}

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 24px;
`;

const UploadedImages = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SecondaryButton = styled(Button)`
  border-color: ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
    background-color: transparent;
    color: ${({ theme }) => theme.colors.black};
  }
  &:disabled {
    &:hover {
      border-color: ${({ theme }) => theme.colors.grayscaleLight};
      color: ${({ theme }) => theme.colors.gray};
      background: ${({ theme }) => hexToRgba(theme.colors.black, 0.05)};
    }
  }
`;

export const RemoveButton = styled.button`
  color: ${({ theme }) => theme.colors.gray};
  padding: 5px;
  transition: color ease 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const ButtonsHolder = styled.div`
  text-align: right;
  margin-top: 20px;
  margin-bottom: 56px;
  button {
    margin-left: 16px;
    &:disabled {
      background: ${({ theme }) => hexToRgba(theme.colors.black, 0.05)};
      border-color: ${({ theme }) => theme.colors.grayscaleLight};
    }
  }
`;

const AddButtonHolder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 80px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayscale};
`;

const AddMoreButton = styled(Text)`
  width: 132px;
  padding: 0 10px;
  font-size: 14px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity ease-out 0.2s;
  border-radius: 4px;
  svg {
    margin-right: 8px;
  }
  &:hover {
    opacity: 0.8;
  }
`;

export interface ImageVariation {
  leftImageSrc: string;
  rightImageSrc: string;
  backgroundSrc?: string;
  foregroundSrc?: string;
  imageTitle?: string;
}

const ThumbnailsGenerator = () => {
  const { teamId } = useParams();

  const [completedGenerationsState, setCompletedGenerationsState] = useState({
    count: 1,
    percentage: 0,
  });

  const [showProgress, setShowProgress] = useState(false);

  const [activeTab, setActiveTab] = useState(Tabs.UploadElements);

  const [activeTypeTab, setActiveTypeTab] = useState(TypeTabs.VerticalSplit);

  const [variations, setVariations] = useState<ImageVariation[]>();

  const [uploadedImages, setUploadedImages] = useState<File[]>([]);

  const [titleInputs, setTitleInputs] = useState<{ [key: string]: string }>({});

  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);

  const [selectedLibraryImages, setSelectedLibraryImages] = useState<number[]>(
    []
  );

  const { data: foregrounds } = useQuery<MediaResource[]>(
    ["generator_foregrounds", { teamId }],
    () =>
      api.get<never, MediaResource[]>(`/v1/teams/${teamId}/media`, {
        params: {
          collectionName: "thumb_foregrounds",
        },
      })
  );

  const selectedForegrounds = foregrounds?.filter((foreground) =>
    selectedLibraryImages.includes(foreground.id)
  );

  const { data: backgrounds } = useQuery<MediaResource[]>(
    ["generator_backgrounds", { teamId }],
    () =>
      api.get<never, MediaResource[]>(`/v1/teams/${teamId}/media`, {
        params: {
          collectionName: "thumb_backgrounds",
        },
      })
  );

  const selectedBackgrounds = backgrounds?.filter((background) =>
    selectedLibraryImages.includes(background.id)
  );

  useEffect(() => {
    const foregroundsIds =
      foregrounds?.map((foreground) => foreground.id) ?? [];

    const backgroundsIds =
      backgrounds?.map((background) => background.id) ?? [];

    setSelectedLibraryImages([...foregroundsIds, ...backgroundsIds]);
  }, [backgrounds, foregrounds]);

  const [modalType, setModalType] = useState<"Backgrounds" | "Foregrounds">(
    "Backgrounds"
  );

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const mappedImages = uploadedImages.map((image) =>
      URL.createObjectURL(image)
    );

    let arr = [];

    for (let i = 0; i < mappedImages.length; i++) {
      for (let j = 0; j < i; j++) {
        if (!selectedBackgrounds?.length && !selectedForegrounds?.length) {
          const variation = {
            leftImageSrc: mappedImages[i],
            rightImageSrc: mappedImages[j],
            imageTitle:
              titleInputs[`imageTitle${j}`] && titleInputs[`imageTitle${i}`]
                ? `${titleInputs[`imageTitle${i}`]}_${
                    titleInputs[`imageTitle${j}`]
                  }.png`
                : `image_${i}_${j}.png`,
          };

          arr.push(variation);
        } else if (
          !selectedBackgrounds?.length &&
          selectedForegrounds?.length
        ) {
          for (let f = 0; f < selectedForegrounds!.length; f++) {
            const variation = {
              leftImageSrc: mappedImages[i],
              rightImageSrc: mappedImages[j],
              foregroundSrc: selectedForegrounds![f].localUrl!,
              imageTitle:
                titleInputs[`imageTitle${j}`] && titleInputs[`imageTitle${i}`]
                  ? `${titleInputs[`imageTitle${i}`]}_${
                      titleInputs[`imageTitle${j}`]
                    }${f}.png`
                  : `image_${i}_${j}_${f}.png`,
            };

            arr.push(variation);
          }
        } else if (
          selectedBackgrounds?.length &&
          !selectedForegrounds?.length
        ) {
          for (let b = 0; b < selectedBackgrounds!.length; b++) {
            const variation = {
              leftImageSrc: mappedImages[i],
              rightImageSrc: mappedImages[j],
              backgroundSrc: selectedBackgrounds![b].localUrl!,
              imageTitle:
                titleInputs[`imageTitle${j}`] && titleInputs[`imageTitle${i}`]
                  ? `${titleInputs[`imageTitle${i}`]}_${
                      titleInputs[`imageTitle${j}`]
                    }_${b}.jpg`
                  : `image_${i}_${j}_${b}.jpg`,
            };

            arr.push(variation);
          }
        } else {
          for (let b = 0; b < selectedBackgrounds!.length; b++) {
            for (let f = 0; f < selectedForegrounds!.length; f++) {
              const variation = {
                leftImageSrc: mappedImages[i],
                rightImageSrc: mappedImages[j],
                backgroundSrc: selectedBackgrounds![b].localUrl!,
                foregroundSrc: selectedForegrounds![f].localUrl!,
                imageTitle:
                  titleInputs[`imageTitle${j}`] && titleInputs[`imageTitle${i}`]
                    ? `${titleInputs[`imageTitle${i}`]}_${
                        titleInputs[`imageTitle${j}`]
                      }_${b}${f}.jpg`
                    : `image_${i}_${j}_${b}_${f}.jpg`,
              };

              arr.push(variation);
            }
          }
        }
        const newArray = [...mappedImages].reverse();
        if (!selectedBackgrounds?.length && !selectedForegrounds?.length) {
          for (let i = 0; i < newArray.length; i++) {
            for (let j = 0; j < i; j++) {
              const variation = {
                leftImageSrc: newArray[i],
                rightImageSrc: newArray[j],
                imageTitle:
                  titleInputs[`imageTitle${j}`] && titleInputs[`imageTitle${i}`]
                    ? `${titleInputs[`imageTitle${j}`]}_${
                        titleInputs[`imageTitle${i}`]
                      }}.png`
                    : `image_${j}_${i}}.png`,
              };

              arr.push(variation);
            }
          }
        } else if (
          !selectedBackgrounds?.length &&
          selectedForegrounds?.length
        ) {
          for (let f = 0; f < selectedForegrounds!.length; f++) {
            for (let i = 0; i < newArray.length; i++) {
              for (let j = 0; j < i; j++) {
                const variation = {
                  leftImageSrc: newArray[i],
                  rightImageSrc: newArray[j],
                  foregroundSrc: selectedForegrounds![f].localUrl!,
                  imageTitle:
                    titleInputs[`imageTitle${j}`] &&
                    titleInputs[`imageTitle${i}`]
                      ? `${titleInputs[`imageTitle${j}`]}_${
                          titleInputs[`imageTitle${i}`]
                        }_${f}.png`
                      : `image_${j}_${i}}_${f}.png`,
                };

                arr.push(variation);
              }
            }
          }
        } else if (
          selectedBackgrounds?.length &&
          !selectedForegrounds?.length
        ) {
          for (let b = 0; b < selectedBackgrounds!.length; b++) {
            for (let i = 0; i < newArray.length; i++) {
              for (let j = 0; j < i; j++) {
                const variation = {
                  leftImageSrc: newArray[i],
                  rightImageSrc: newArray[j],
                  backgroundSrc: selectedBackgrounds![b].localUrl!,
                  imageTitle:
                    titleInputs[`imageTitle${j}`] &&
                    titleInputs[`imageTitle${i}`]
                      ? `${titleInputs[`imageTitle${j}`]}_${
                          titleInputs[`imageTitle${i}`]
                        }_${b}.jpg`
                      : `image_${j}_${i}_${b}.jpg`,
                };

                arr.push(variation);
              }
            }
          }
        } else {
          for (let b = 0; b < selectedBackgrounds!.length; b++) {
            for (let f = 0; f < selectedForegrounds!.length; f++) {
              for (let i = 0; i < newArray.length; i++) {
                for (let j = 0; j < i; j++) {
                  const variation = {
                    leftImageSrc: newArray[i],
                    rightImageSrc: newArray[j],
                    backgroundSrc: selectedBackgrounds![b].localUrl!,
                    foregroundSrc: selectedForegrounds![f].localUrl!,
                    imageTitle:
                      titleInputs[`imageTitle${j}`] &&
                      titleInputs[`imageTitle${i}`]
                        ? `${titleInputs[`imageTitle${j}`]}_${
                            titleInputs[`imageTitle${i}`]
                          }_${b}${f}.jpg`
                        : `image_${j}_${i}_${b}_${f}.jpg`,
                  };

                  arr.push(variation);
                }
              }
            }
          }
        }
      }
    }

    setVariations(arr);
  };

  const onAddMore = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files?.length) return;

    Array.from(files).forEach((element) => {});

    const uploadedImagesNames = uploadedImages.map(
      (uploadedImage) => uploadedImage.name
    );

    setUploadedImages((prev) => [
      ...prev,
      ...Array.from(files).filter(
        (file) => !uploadedImagesNames.includes(file.name)
      ),
    ]);
  };

  return (
    <>
      <HeadingRow>
        <PageHeading>Thumbnails Generator</PageHeading>
        {activeTab === Tabs.BackgroundLibrary && (
          <Button
            onClick={() => {
              setModalType("Backgrounds");
              setIsUploadModalVisible(true);
            }}
          >
            Upload
          </Button>
        )}
        {activeTab === Tabs.ForegroundLibrary && (
          <Button
            onClick={() => {
              setModalType("Foregrounds");
              setIsUploadModalVisible(true);
            }}
          >
            Upload
          </Button>
        )}
      </HeadingRow>
      <TableHeader>
        <GeneratedThumbnaisStyles />
        <Controls>
          <ControlsButton
            active={activeTab === Tabs.UploadElements}
            onClick={() => setActiveTab(Tabs.UploadElements)}
          >
            Upload Elements
          </ControlsButton>

          <ControlsButton
            active={activeTab === Tabs.ForegroundLibrary}
            onClick={() => setActiveTab(Tabs.ForegroundLibrary)}
          >
            Foreground Library
          </ControlsButton>

          <ControlsButton
            active={activeTab === Tabs.BackgroundLibrary}
            onClick={() => setActiveTab(Tabs.BackgroundLibrary)}
          >
            Background Library
          </ControlsButton>
        </Controls>
        {activeTab === Tabs.UploadElements && (
          <Controls>
            <ControlsButton
              active={activeTypeTab === TypeTabs.VerticalSplit}
              onClick={() => setActiveTypeTab(TypeTabs.VerticalSplit)}
            >
              Vertical Split
            </ControlsButton>
          </Controls>
        )}
      </TableHeader>
      {activeTab === Tabs.UploadElements && (
        <>
          {!!!uploadedImages?.length && (
            <ThumbnailsUploadFiles setUploadedImages={setUploadedImages} />
          )}
          {!!uploadedImages?.length && (
            <Box>
              <UploadedImages>
                {uploadedImages?.map((file, index) => (
                  <ThumbnailsImageRow
                    key={index}
                    index={index}
                    file={file}
                    setTitleInputs={setTitleInputs}
                    title={titleInputs[`imageTitle${index}`]}
                    setUploadedImages={setUploadedImages}
                  />
                ))}
                <AddButtonHolder>
                  <AddMoreButton
                    as="label"
                    $type="smallBold"
                    htmlFor="images-input"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 1V13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M1 7H13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Add More
                    <SrOnly
                      as="input"
                      type="file"
                      id="images-input"
                      accept="image/png"
                      multiple
                      onChange={onAddMore}
                    />
                  </AddMoreButton>
                </AddButtonHolder>
              </UploadedImages>
            </Box>
          )}
          <ButtonsHolder>
            {(!!variations?.length || !!uploadedImages.length) && (
              <SecondaryButton
                onClick={() => {
                  setVariations([]);
                  setUploadedImages([]);
                }}
              >
                Clear
              </SecondaryButton>
            )}
            {!!uploadedImages?.length && (
              <Button
                disabled={uploadedImages.length < 2}
                onClick={generateVariations}
              >
                Generate
              </Button>
            )}
          </ButtonsHolder>
          {!!variations?.length && (
            <ThumbnailsGeneratedVariations
              variations={variations}
              setCompletedGenerationsState={setCompletedGenerationsState}
              setShowProgress={setShowProgress}
            />
          )}
        </>
      )}
      {activeTab === Tabs.ForegroundLibrary && (
        <ThumbnailsLibrary
          type="foreground"
          images={foregrounds}
          selectedLibraryImages={selectedLibraryImages}
          setSelectedLibraryImages={setSelectedLibraryImages}
        />
      )}
      {activeTab === Tabs.BackgroundLibrary && (
        <ThumbnailsLibrary
          type="background"
          images={backgrounds}
          selectedLibraryImages={selectedLibraryImages}
          setSelectedLibraryImages={setSelectedLibraryImages}
        />
      )}
      <AnimatePresence>
        {isUploadModalVisible && (
          <ThumbnailsUploadForm
            type={modalType}
            onClose={() => setIsUploadModalVisible(false)}
          />
        )}
        {!!showProgress && (
          <ThumbnailsDownloadProgress
            progressPercent={completedGenerationsState.percentage}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ThumbnailsGenerator;
